
import { autoinject, bindable } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/essette-api';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { EventAggregator } from 'aurelia-event-aggregator';
import { ImportStatusAndHistoryReloadEvent } from 'app/um/lib/events';
import { ImportsService } from 'app/core/services/imports-service';
import { openJobDetailSummary } from 'app/core/components/summary/job/job-detail-summary';
import * as Core from 'app/core/models/essette-api';
import { guid } from 'app/shared/lib/guid';


@autoinject()
export class ImportStatusAndHistoryGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    @bindable({ changeHandler: 'reload' })
    protected importJobs: ReadonlyArray<Core.ImportJob> = [];

    protected allowDelete: boolean = false;
    protected allowReset: boolean = false;
    protected showStagedJobSummaryLink: boolean = false;

    protected openJobDetailSummary = openJobDetailSummary;
    protected id: guid = guid();
    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: ImportsService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);

    }

    protected bind(): void {
        this.checkPermissions();
        this.reload();
    }

    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.allowDelete = this.accountContext.hasPermission('Core.ImportHistory.Remove.DeleteStaged');
        this.allowReset = this.accountContext.hasPermission('Core.ImportHistory.Reset.ResetJob');
        this.showStagedJobSummaryLink = this.accountContext.hasRouteAccess('core.imports.job.summary');
    }

    protected getStatusClass(status: string) {
        let statusClass = '';
        switch(status) {
            case 'Staging':
                statusClass = 'status-color-stage'
                break;
            case 'Staged':
                statusClass = 'status-color-stage'
                break;
            case 'Queued':
                statusClass = 'status-color-accent'
                break;
            case 'Running':
                statusClass = 'status-color-secondary'
                break;
            case 'Has Errors':
                statusClass = 'status-color-danger'
                break;
            case 'Completed':
                statusClass = 'status-color-success'
                break;
        }

        return statusClass;
    }

    protected getStatusIcon(status: string) {
        let iconResource = '';
        switch(status) {
            case 'Staging':
                iconResource = ''
                break;
            case 'Staged':
                iconResource = ''
                break;
            case 'Queued':
                iconResource = 'ui:icons.time'
                break;
            case 'Running':
                iconResource = 'ui:icons.running'
                break;
            case 'Has Errors':
                iconResource = 'ui:icons.warning'
                break;
            case 'Completed':
                iconResource = 'ui:icons.done'
                break;
        }

        return iconResource;
    }

    protected buildImportHistory(job: Core.ImportJob): Core.ImportHistory {
        const importHistory: Core.ImportHistory = {
            systemCode: job.stagedSystemCode,
            stagedJobIdentifier: job.stagedJobIdentifier
        }

        return importHistory;
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    public async removeImportHistory(importJob: Core.ImportJob) {
        if(!this.allowDelete || !importJob["stagedSystemCode"] || !importJob["stagedJobIdentifier"]) return;

        const result = await confirmation.showDelete('core:importHistory.job.$', 1, false);
        if(result.dialogResponse === confirmation.options.CANCEL) { return; }

        if(!(result.dialogResponse === confirmation.options.OK)) {
            return;
        }

        let isSuccessful: boolean = false;
        try {
            this.state.beginSave();
            const response = await this.service.removeImportHistory(this.buildImportHistory(importJob));

            isSuccessful = response.isSuccessful;
            if(isSuccessful) {
                this.events.publish(new ImportStatusAndHistoryReloadEvent());
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.deleted('core:importHistory.job.$', isSuccessful);
            this.state.endSave();
        }
    }


    @apiCall
    public async resetImportHistory(importJob: Core.ImportJob) {
        if(!this.allowReset || !importJob["stagedSystemCode"] || !importJob["stagedJobIdentifier"]) return;

        let isSuccessful: boolean = false;
        try {
            this.state.beginSave();
            const response = await this.service.resetImportHistory(this.buildImportHistory(importJob));

            isSuccessful = response.isSuccessful;
            if(isSuccessful) {
                this.events.publish(new ImportStatusAndHistoryReloadEvent());
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            const toast = isSuccessful ? this.toaster.info() : this.toaster.error();
            toast.message({
                key: isSuccessful ? 'ui:messages.events.retry' : 'ui:messages.events.retryFailed',
                count: 1,
                what: 'core:importHistory.job.$'
            }).show();
            this.state.endSave();
        }
    }

}
